<template>
  <div class="py-4 h-100">
    <div
      class="row h-100"
      v-if="currentUserCanView"
    >
      <div class="col h-100">
        <div
          class="my-5 text-center"
          v-if="loading"
          key="loading"
        >
          <h6 class="text-muted">{{ $t('vendorPerformance.loading') }}</h6>
        </div>
        <div
          v-else
          class="h-100"
        >
          <object-grid
            v-if="hasSingleSystem"
            :model-class="'VendorScorecardRun'"
            :flow-subtype="systems[0].slug"
            class="h-100"
            grid-max-height="100%"
            :col-state-local-storage-key="localStorageKey"
            :exclude-fields="scorecardRunGridExcludeFields"
            :fixed-filter-kwargs="fixedFilterKwargs"
          ></object-grid>
          <b-tabs
            v-else-if="hasSystems"
            key="systemsTabs"
            class="h-100"
            no-nav-style
            nav-class="spl-floating-nav"
            nav-wrapper-class="d-flex"
            content-class="mt-3 h-100"
            lazy
          >
            <b-tab
              v-for="system in systems"
              :key="system.id"
              :title="system.name"
              class="h-100"
            >
              <object-grid
                model-class="VendorScorecardRun"
                :flow-subtype="system.slug"
                class="h-100"
                grid-max-height="100%"
                :col-state-local-storage-key="localStorageKey"
                :exclude-fields="scorecardRunGridExcludeFields"
                :fixed-filter-kwargs="fixedFilterKwargs"
              ></object-grid>
            </b-tab>
          </b-tabs>
          <div
            class="my-5 text-center"
            v-else
            key="noSystems"
          >
            <h6>{{ $t('vendorPerformance.noScorecards') }}</h6>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="my-5 w-100 min-height-150 d-flex justify-content-center align-items-center"
    >
      <div class="font-weight-bold text-muted font-size-100"> {{ $t('vendorPerformance.notAvailable') }} </div>
    </div>
  </div>
</template>

<script>
  import { suppliosApi } from '@/js/api-service';
  import { errorHandling, currentUserInfoMixin } from '@/js/custom-vue-mixins';

  export default {
    props: {
      resource: {
        type: Object,
        required: true,
      },
    },
    mixins: [errorHandling, currentUserInfoMixin],
    i18n: {
      messages: {
        en: {
          vendorPerformance: {
            loading: 'Loading...',
            noScorecards: 'No scorecards to show',
            notAvailable: 'Scorecards are not available based on your permissions.',
          },
        },
        es: {
          vendorPerformance: {
            loading: 'Cargando...',
            noScorecards: 'No hay scorecards para mostrar',
            notAvailable: 'Los scorecards no están disponibles según sus permisos.',
          },
        },
        pt: {
          vendorPerformance: {
            loading: 'Carregando...',
            noScorecards: 'Nenhum scorecard para exibir',
            notAvailable: 'Os scorecards não estão disponíveis com base nas suas permissões.',
          },
        },
        'zh-Hans': {
          vendorPerformance: {
            loading: '加载中...',
            noScorecards: '没有记分卡可显示',
            notAvailable: '根据您的权限，无法查看记分卡。',
          },
        },
        'zh-Hant': {
          vendorPerformance: {
            loading: '載入中...',
            noScorecards: '沒有評分卡可顯示',
            notAvailable: '根據您的權限，無法檢視評分卡。',
          },
        },
      },
    },
    data() {
      return {
        loading: false,
        systems: [], // all performance systems
        //selectedSystem: 'summary',
        localStorageKey: 'vendorProfile',  // this is part of unique key for storing col config in localStorage - we will share the config across all vendors, but separate from full object grid in main scorecard list
        scorecardRunGridExcludeFields: ['data_run', 'vendor'],  // can exclude vendor also since we have fixedFilterKwargs
        fixedFilterKwargs: { // replaces the vendorFilter and fixedFilterModel
          vendor: this.resource.id,
        },
      };
    },
    computed: {
      hasSystems() {
        return !!this.systems?.length;
      },
      hasSingleSystem() {
        return this.systems.length === 1;
      },
      systemsById() {
        return Object.fromEntries(this.systems.map((scs) => [scs.id, scs]));
      },
      systemIds() {
        return this.systems.map((scs) => scs.id);
      },
      currentUserCanView() {
        // can possibly remove this if object roles / visibility rules are set properly in flowdefs on systems
        return this.$currentUserInfo.is_internal;  // we had is_paid_seat before... not sure why....
      },
    },
    mounted() {
      this.getSystems();
    },
    methods: {
      async getSystems() {
        this.loading = true;
        let resp;
        try {
          resp = await suppliosApi.performanceSystem.list_all_systems();  // special endpoint since this is self-scoped
        } catch (error) {
          this.apiErrorHandler(error);
        }
        if (resp?.data) {
          this.systems = resp.data; // not paginated
        }
        this.loading = false;
      },
    },
  };
</script>
